import { queryOptions } from "@tanstack/react-query";
import {
  type MembersPersonalOption,
  fetchAppCategories,
  fetchApprovalCard,
  fetchApprovals,
  fetchCatalog,
  fetchConnection,
  fetchConnections,
  fetchEvents,
  fetchPermissions,
} from "../connections/api";
import { fetchWebsiteLogins } from "../connections/website-logins";
import { fetchSitePreview } from "../site-preview/api";
import type { RegionalSession } from "../regional-request";
import type { AccessScope } from "../regional-token-cache";

/** Keyed by user and workspace so a switch never shows the previous workspace's accounts. */
export const connectionsKey = (scope: AccessScope) =>
  ["connections", scope.userId, scope.workspaceId] as const;

export const catalogQueryOptions = (session: RegionalSession) =>
  queryOptions({
    queryKey: [...connectionsKey(session.scope), "catalog"] as const,
    queryFn: () => fetchCatalog(session),
    retry: false,
    staleTime: 60_000,
  });

/** Which categories an app has; a pinned tool list changes rarely, so it is kept a while. */
export const appCategoriesQueryOptions = (session: RegionalSession, app: string) =>
  queryOptions({
    queryKey: [...connectionsKey(session.scope), "app-categories", app] as const,
    queryFn: () => fetchAppCategories(session, app),
    retry: false,
    staleTime: 5 * 60_000,
  });

/**
 * The part of a key that says "and members' personal connections too". Absent when off, so the keys
 * every caller already used are unchanged, and both sit under `connectionsKey`, so one
 * invalidation clears the two reads.
 */
const widening = (options: MembersPersonalOption | undefined) =>
  options?.membersPersonal ? ([{ membersPersonal: true }] as const) : ([] as const);

/**
 * Keeps what a list showed on screen while the members switch widens it, so the rows do not blink
 * to a skeleton: every row of the plain list is in the wider one too. Only while widening
 * (`isWidening`, the flag being asked for): narrowing back must not keep other members' accounts on
 * screen until the plain list lands, so it shows the skeleton instead. Never across a change of
 * user or workspace either: those must not show the previous one's accounts for a moment.
 */
export const keepWhileWidening =
  (scope: AccessScope, isWidening: boolean) =>
  <T>(previous: T | undefined, query: { queryKey: readonly unknown[] } | undefined) =>
    isWidening && query?.queryKey[1] === scope.userId && query.queryKey[2] === scope.workspaceId
      ? previous
      : undefined;

export const connectionsQueryOptions = (
  session: RegionalSession,
  options?: MembersPersonalOption,
) =>
  queryOptions({
    queryKey: [...connectionsKey(session.scope), "list", ...widening(options)] as const,
    queryFn: () => fetchConnections(session, options),
    retry: false,
  });

export const connectionQueryOptions = (
  session: RegionalSession,
  id: string,
  options?: MembersPersonalOption,
) =>
  queryOptions({
    queryKey: [...connectionsKey(session.scope), "detail", id, ...widening(options)] as const,
    queryFn: () => fetchConnection(session, id, options),
    retry: false,
  });

export const permissionsQueryOptions = (session: RegionalSession, id: string) =>
  queryOptions({
    queryKey: [...connectionsKey(session.scope), "permissions", id] as const,
    queryFn: () => fetchPermissions(session, id),
    retry: false,
  });

export const eventsQueryOptions = (session: RegionalSession, id: string) =>
  queryOptions({
    queryKey: [...connectionsKey(session.scope), "events", id] as const,
    queryFn: () => fetchEvents(session, id),
    retry: false,
  });

/** The workspace's open asks; a connection's Activity pins the ones that are its own. */
export const approvalsQueryOptions = (session: RegionalSession) =>
  queryOptions({
    queryKey: [...connectionsKey(session.scope), "approvals"] as const,
    queryFn: () => fetchApprovals(session),
    retry: false,
  });

/**
 * What one chat approval will do, for the chat's page. Its arguments never change in this slice,
 * so a read stays good for the minute a person takes to decide; the status inside it is not what
 * the page acts on (the request is), so it is not refetched on focus.
 */
export const approvalCardQueryOptions = (session: RegionalSession, approvalId: string) =>
  queryOptions({
    queryKey: [...connectionsKey(session.scope), "approval-card", approvalId] as const,
    queryFn: () => fetchApprovalCard(session, approvalId),
    retry: false,
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });

/**
 * The workspace's website logins. Under the same key as the connections, so anything that
 * refreshes the list refreshes these too.
 */
export const websiteLoginsQueryOptions = (
  session: RegionalSession,
  options?: MembersPersonalOption,
) =>
  queryOptions({
    queryKey: [...connectionsKey(session.scope), "website-logins", ...widening(options)] as const,
    queryFn: () => fetchWebsiteLogins(session, options),
    retry: false,
  });

/**
 * A website's icon and name, by its normalized address. Public facts that change rarely, so they
 * are kept for the session and never retried: a site with no icon simply shows its initial.
 */
export const sitePreviewQueryOptions = (session: RegionalSession, url: string) =>
  queryOptions({
    // Public facts, so not per workspace; per user, so a sign-out leaves nothing behind.
    queryKey: ["site-preview", session.scope.userId, url] as const,
    queryFn: () => fetchSitePreview(session, url),
    retry: false,
    staleTime: Number.POSITIVE_INFINITY,
    gcTime: 60 * 60_000,
  });
